/**
 * Run panel controls, built once and kept across panel updates so typing in
 * the instruction box isn't interrupted: Start analysis (when waiting for a
 * go-ahead), Cancel and Add instruction.
 */
import { el } from '../../core/dom.js';

/**
 * @param {{ runId: string, post: Function, onCancel: Function, onStarted: Function }} opts
 * @returns {{ box: HTMLElement, setWaiting: (on: boolean, why?: string) => void }}
 */
export function createRunControls({ runId, post, onCancel, onStarted }) {

  const box = el('div', { class: 'chat-run-controls' });
  const waiting = el('div', { class: 'chat-run-waiting', hidden: true });
  const reason = el('p', { class: 'chat-run-note' });
  const startBtn = el('button', { type: 'button', class: 'chat-chip chat-run-start' }, 'Start analysis');
  const startNote = el('p', { class: 'chat-run-note is-error', hidden: true });
  waiting.append(reason, startBtn, startNote);
  startBtn.addEventListener('click', async () => {
    startBtn.disabled = true;
    const res = await post(`/analysis/${encodeURIComponent(runId)}/confirm`, {});
    startBtn.disabled = false;
    if (res && !res.error) {
      onStarted(res);
      return;
    }
    startNote.hidden = false;
    startNote.textContent = (res && res.error) ? String(res.error) : 'Could not start.';
  });

  const buttons = el('div', { class: 'chat-run-buttons' });
  const cancelBtn = el('button', { type: 'button', class: 'chat-chip chat-run-cancel' }, 'Cancel');
  const steerBtn = el('button', { type: 'button', class: 'chat-chip chat-run-steer' }, 'Add instruction');
  buttons.append(cancelBtn, steerBtn);
  cancelBtn.addEventListener('click', onCancel);

  const input = el('textarea', {
    class: 'chat-run-steer-input', rows: '2', maxlength: '1000',
    'aria-label': 'Instruction for this analysis',
  });
  const send = el('button', { type: 'button', class: 'chat-chip chat-run-steer-send' }, 'Send');
  const note = el('p', { class: 'chat-run-note', hidden: true });
  const steerBox = el('div', { class: 'chat-run-steer-box', hidden: true }, input, send, note);
  steerBtn.addEventListener('click', () => {
    steerBox.hidden = !steerBox.hidden;
    if (!steerBox.hidden) input.focus();
  });
  send.addEventListener('click', async () => {
    const text = String(input.value || '').trim();
    if (!text) return;
    send.disabled = true;
    const res = await post(`/analysis/${encodeURIComponent(runId)}/steer`, { text });
    send.disabled = false;
    note.hidden = false;
    if (res && !res.error) {
      input.value = '';
      note.textContent = 'Instruction sent. It applies at the next step.';
      note.setAttribute('class', 'chat-run-note');
      return;
    }
    note.textContent = (res && res.error) ? String(res.error) : 'Could not send.';
    note.setAttribute('class', 'chat-run-note is-error');
  });

  box.append(waiting, buttons, steerBox);
  return {
    box,
    /** Show "Start analysis" only while the run waits for confirmation. */
    setWaiting(on, why) {
      waiting.hidden = !on;
      if (on) reason.textContent = why || '';
    },
  };
}
